import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { MultiBuyerCmpAuth } from '../../../auth/auth.interface';
import { AuthService } from '../../../auth/auth.service';
import {
  AccessControlConfig,
  AccessibleEntity,
  EntityType,
  isMultiBuyerRole,
  MultiBuyerRole,
  MULTI_BUYER_ROLES_NUM_MASK,
} from './interfaces/multi-buyer-company.interface';
import { AccessConfig } from './access-config/access-config';

@Injectable({ providedIn: 'root' })
export class MultiBuyerStateService {
  private readonly _baseUrl = environment.pureSpectrum.url;

  private readonly _selectedMultiBuyerCompany$ = new BehaviorSubject<
    MultiBuyerCmpAuth | undefined
  >(undefined);
  readonly selectedMultiBuyerCompany$ =
    this._selectedMultiBuyerCompany$.asObservable();

  private readonly _accessControlConfig$ = new BehaviorSubject<AccessConfig>(
    AccessConfig.uninitialized()
  );
  readonly accessControlConfig$ = this._accessControlConfig$.asObservable();

  private readonly _loggedInAsMultiBuyer$ = new BehaviorSubject<boolean>(false);
  readonly loggedInAsMultiBuyer$ = this._loggedInAsMultiBuyer$.asObservable();

  private readonly _multiBuyerModalCloseRequests$ = new Subject<{
    source: 'dashboard' | 'auth' | 'unknown';
  }>();
  readonly multiBuyerModalCloseRequests$ =
    this._multiBuyerModalCloseRequests$.asObservable();

  constructor(
    private readonly _http: HttpClient,
    private readonly _injector: Injector
  ) {}

  private get _auth(): AuthService {
    return this._injector.get(AuthService);
  }

  get selectedMultiBuyerCompany(): MultiBuyerCmpAuth | undefined {
    return this._selectedMultiBuyerCompany$.getValue();
  }

  get accessControlConfig(): AccessConfig {
    return this._accessControlConfig$.getValue();
  }

  get multiBuyerCmpsIds(): number[] {
    const { accessibleEntities, multiBuyerCmps } = this.accessControlConfig;
    const entities: AccessibleEntity[] = accessibleEntities.length
      ? accessibleEntities
      : multiBuyerCmps.map((id) => ({ id, type: EntityType.Owned }));
    return this._filterEntities(entities);
  }

  private _filterEntities(entities: AccessibleEntity[]): number[] {
    const includeSP = this.isLoggedInAsMbManager();
    return entities
      .filter(
        (e) =>
          e.type === EntityType.Owned ||
          (includeSP && e.type === EntityType.ServicePartner)
      )
      .map((e) => e.id);
  }

  isLoggedInAsMultiBuyer(): boolean {
    return isMultiBuyerRole(this.accessControlConfig.userRole);
  }

  isLoggedInAsMasterMultiBuyer(): boolean {
    return (
      this.accessControlConfig.userRole === MultiBuyerRole.MasterMultiBuyer
    );
  }

  isLoggedInAsMbManager(): boolean {
    return this.accessControlConfig.userRole === MultiBuyerRole.MbManager;
  }

  isSelectedMultiBuyerCompany(): boolean {
    return !!this._selectedMultiBuyerCompany$.getValue();
  }

  isConfigReady(): boolean {
    return this._accessControlConfig$.getValue().isReady;
  }

  setSelectedCompany(company: MultiBuyerCmpAuth | undefined): void {
    this._selectedMultiBuyerCompany$.next(company);
  }

  setAccessControlConfig(config: AccessConfig): void {
    this._accessControlConfig$.next(config);
    this._loggedInAsMultiBuyer$.next(this.isLoggedInAsMultiBuyer());
  }

  getNumericMultiBuyerRole(userRole: MultiBuyerRole): number {
    return MULTI_BUYER_ROLES_NUM_MASK[userRole];
  }

  requestCloseMultiBuyerModal(request?: {
    source?: 'dashboard' | 'auth' | 'unknown';
  }): void {
    this._multiBuyerModalCloseRequests$.next({
      source: request?.source ?? 'unknown',
    });
  }

  getAccessControlConfig(
    clientId: number,
    userRole: MultiBuyerRole
  ): Observable<AccessControlConfig> {
    const convertedMultiBuyerUserRole = this.getNumericMultiBuyerRole(userRole);
    return this._http
      .get<AccessControlConfig>(
        `${this._baseUrl}/buyers/v2/configs/${clientId}/${convertedMultiBuyerUserRole}`
      )
      .pipe(map((config) => ({ ...config, userRole })));
  }
}
